import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImageUploader from './components/ImageUploader';

const { FiImage, FiVideo, FiFile, FiTrash2, FiCopy, FiDownload, FiSearch, FiGrid, FiList } = FiIcons;

const MediaManager = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    loadMediaFiles();
  }, []);

  const loadMediaFiles = () => {
    const files = JSON.parse(localStorage.getItem('media_files') || '[]');
    setMediaFiles(files.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)));
  };

  const handleFileUpload = (fileUrl, fileName, fileType) => {
    const newFile = {
      id: Date.now().toString(),
      url: fileUrl,
      name: fileName || `media-${Date.now()}`,
      type: fileType || 'image',
      size: Math.floor(Math.random() * 1000000), // Simulated file size
      uploadedAt: new Date().toISOString(),
      usedIn: []
    };

    const updatedFiles = [newFile, ...mediaFiles];
    setMediaFiles(updatedFiles);
    localStorage.setItem('media_files', JSON.stringify(updatedFiles));
  };

  const handleDeleteFile = (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const updatedFiles = mediaFiles.filter(file => file.id !== fileId);
      setMediaFiles(updatedFiles);
      localStorage.setItem('media_files', JSON.stringify(updatedFiles));
    }
  };

  const handleBulkDelete = () => {
    if (selectedFiles.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedFiles.length} files?`)) {
      const updatedFiles = mediaFiles.filter(file => !selectedFiles.includes(file.id));
      setMediaFiles(updatedFiles);
      localStorage.setItem('media_files', JSON.stringify(updatedFiles));
      setSelectedFiles([]);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    // You could add a toast notification here
    alert('URL copied to clipboard!');
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'video':
        return FiVideo;
      case 'image':
        return FiImage;
      default:
        return FiFile;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredFiles = mediaFiles.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || file.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Media Library</h2>
        <div className="flex items-center space-x-3">
          {selectedFiles.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-300"
            >
              <SafeIcon icon={FiTrash2} className="h-4 w-4" />
              <span>Delete ({selectedFiles.length})</span>
            </button>
          )}
          <div className="flex items-center space-x-1 bg-white border border-gray-300 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'grid' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiGrid} className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'list' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiList} className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload New Media</h3>
        <ImageUploader
          onUpload={(url) => handleFileUpload(url, null, 'image')}
          label="Upload images, videos, or other files"
          multiple
          acceptAll
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search media files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Files</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="document">Documents</option>
          </select>
        </div>
      </div>

      {/* Media Grid/List */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        {filteredFiles.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-6">
              {filteredFiles.map((file) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="group relative bg-white rounded-lg overflow-hidden hover:shadow-md transition-all duration-200 border border-gray-200"
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => toggleFileSelection(file.id)}
                      className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                  </div>

                  {/* File Preview */}
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {file.type === 'image' ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <SafeIcon icon={getFileIcon(file.type)} className="h-8 w-8 text-gray-400" />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(file.url)}
                        className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                        title="Copy URL"
                      >
                        <SafeIcon icon={FiCopy} className="h-4 w-4 text-gray-700" />
                      </button>
                      <a
                        href={file.url}
                        download={file.name}
                        className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                        title="Download"
                      >
                        <SafeIcon icon={FiDownload} className="h-4 w-4 text-gray-700" />
                      </a>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                        title="Delete"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredFiles.map((file) => (
                <div key={file.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => toggleFileSelection(file.id)}
                      className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {file.type === 'image' ? (
                        <img
                          src={file.url}
                          alt={file.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <SafeIcon icon={getFileIcon(file.type)} className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => copyToClipboard(file.url)}
                        className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                        title="Copy URL"
                      >
                        <SafeIcon icon={FiCopy} className="h-4 w-4" />
                      </button>
                      <a
                        href={file.url}
                        download={file.name}
                        className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                        title="Download"
                      >
                        <SafeIcon icon={FiDownload} className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="p-2 text-red-500 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="p-12 text-center">
            <SafeIcon icon={FiImage} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No media files found</p>
            <p className="text-gray-500">Upload some files to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaManager;